import { BasketItemSource } from '@epicuro-next/models/basket';
import { shopTheList } from '@epicuro-next/state/product-list-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';

import * as actions from '../actions';
import {
  BackgroundProductState,
  BusinessState,
  DeliveryItemsByShop,
  ProductQuantitiesChange,
  ProductVariant,
} from '../models/config-strip-state.models';
import { getSelectedDeliveryPeriod } from '../shared/get-selected-delivery-period';

import { cachedBusiness, cachedProductDataBusiness } from './cached.reducer';
import {
  addBulkDeliveryPeriodBusiness,
  addFutureDeliveryPeriodBusiness,
  copyDeliveryPeriodsFromCacheBusiness,
  deleteBulkDeliveryPeriodBusiness,
  deleteFutureDeliveryPeriodBusiness,
  preselectDeliveryPeriodsBusiness,
  preSelectNowDeliveryPeriodBusiness,
  selectBulkDeliveryPeriodBusiness,
  selectFutureDeliveryPeriodBusiness,
  selectNowDeliveryPeriodBusiness,
} from './delivery-periods';
import { copyToDeliveryPeriodBusiness } from './delivery-periods/copy-to-delivery-period';
import { addNowDeliveryPeriodReducerNew } from './delivery-periods/future-delivery-periods/add-now-delivery-period.reducer';
import {
  setMasterPrefilledDeliveryItemsBusiness,
  setPrefilledCopyBusiness,
  setPrefilledDeliveryItemsBusiness,
  setPrefilledVisibleBusiness,
} from './prefilled-delivery-items.reducer';
import { copyToShopBusiness } from './quantities/copy-to-shop';
import { productNoteChangeBusiness } from './quantities/product-note.reducer';
import {
  productQuantitiesBusiness,
  productQuantityChangeBusiness,
} from './quantities/quantities-change/product-quantity.reducer';
import { revertShopBusiness } from './quantities/revert-shop';
import { setHorizontalView } from '@epicuro-next/state/p-basket-state';

export const backgroundProductsAdapter =
  createEntityAdapter<BackgroundProductState>({
    selectId: (entity) => entity?.productParentId ?? entity.productId,
  });

interface IKeyValue {
  [key: string]: string;
}

const cachedStateKey = 'cached';
const backgroundProductsKey = 'backgroundProducts';
const nextButtonKey = 'showNextButton';
const prefilledDeliveryItemsKey = 'prefilledDeliveryItems';
const itemSourceKey = 'source';
const activeShopsKey = 'activeDeliveryShops';
const activeDeliveryShopKey = 'activeDeliveryShop';

export const businessStateKeys = [
  // cachedStateKey,
  // backgroundProductsKey,
  nextButtonKey,
  // prefilledDeliveryItemsKey,
  itemSourceKey,
  activeShopsKey,
];

export const businessInitialState: BusinessState = {
  [prefilledDeliveryItemsKey]: {},
  [backgroundProductsKey]: backgroundProductsAdapter.getInitialState(),
  deliveryItems: {},
  deliveryPeriods: {},
  nowDeliveryPeriods: undefined,
  usedInventories: {},
  modifiedProducts: {},
  persistingChanges: false,
  skipInventoryCheck: false,
  totalQuantity: 0,
  totalPrices: {
    wsp: 0,
    rrp: 0,
  },
  dirty: false,
  navigateToNext: false,
  memoProductSizes: {},
  [nextButtonKey]: false,
  [itemSourceKey]: {
    type: BasketItemSource.EMPTY,
    details: {},
  },
  cached: {},
  [activeShopsKey]: [],
  [activeDeliveryShopKey]: { accountId: '', addressId: '' },
  isSetSourceNeed: false,
};

export const getScaleSets = (product: Product) => {
  const defaultScaleSets = product?.scaleSets?.default ?? '0';
  return Object.keys(product?.scaleSets?.scales[defaultScaleSets] ?? {});
};

export const getScaleKey = (attributes: IKeyValue, scales: string[]) => {
  return (
    scales.reduce(
      (acc, scale) =>
        (!acc ? '' : `${acc}-`) + (attributes[scale] ?? 'default'),
      '',
    ) ?? ''
  );
};
/*
 * transforms map
 * from { [sizeId]: quantity } (e.g. { '1234': 2 })
 * to { [size-width]: quantity } (e.g. {'24-"2': 2 })
 * */
export const mapSizeIdQuantitiesToSizeQuantities = (
  variants: ProductVariant,
  deliveryItems: DeliveryItemsByShop,
  fromShopId: string,
  deliveryPeriod: string,
  scales: string[],
) => {
  return variants?.sizeReferences.reduce(
    (result: { [key: string]: number }, sizeReference: string) => {
      // map sizeReference to actual size
      const attributes =
        variants?.children?.find((size) => size.productId === sizeReference)
          ?.attributes ?? {};

      const concreteSize = getScaleKey(attributes as IKeyValue, scales);
      result[concreteSize] =
        deliveryItems[fromShopId]?.[deliveryPeriod]?.items[sizeReference]
          ?.quantity ?? 0;

      return result;
    },
    {},
  );
};

export const getSizeIdFromSize = (
  variants: ProductVariant[],
  size: string,
  scales: string[],
) => {
  const targetSize = variants.find(
    (variant) => getScaleKey(variant.attributes as IKeyValue, scales) === size,
  );
  return targetSize?.productId;
};

export const businessConfigStripReducer = createReducer<BusinessState>(
  businessInitialState,
  on(actions.loadProduct, (state: BusinessState) => ({
    ...businessInitialState,
    [prefilledDeliveryItemsKey]: state.prefilledDeliveryItems,
    [backgroundProductsKey]: state.backgroundProducts,
    dirty: false,
    showNextButton: state.showNextButton,
    skipInventoryCheck: state.skipInventoryCheck,
    source: state.source,
    memoProductSizes: state.memoProductSizes,
    persistingChanges: state.persistingChanges ?? false,
    [activeShopsKey]: state[activeShopsKey],
    [cachedStateKey]: state[cachedStateKey],
    [activeDeliveryShopKey]: state[activeDeliveryShopKey],
  })),
  on(actions.clearState, (state: BusinessState) => ({
    ...businessInitialState,
    memoProductSizes: state.memoProductSizes,
    [backgroundProductsKey]: state.backgroundProducts,
    [prefilledDeliveryItemsKey]: state.prefilledDeliveryItems,
    source: state.source,
    skipInventoryCheck: state.skipInventoryCheck,
    [activeShopsKey]: state[activeShopsKey],
    [cachedStateKey]: state[cachedStateKey],
  })),
  on(actions.setActiveDeliveryShop, (state, { addressId, accountId }) => ({
    ...state,
    activeDeliveryShop: {
      addressId,
      accountId,
    },
  })),
  on(actions.persistChanges, (state) => ({
    ...state,
    persistingChanges: true,
  })),
  on(actions.persistChangesSuccess, actions.persistChangesFailure, (state) => {
    return {
      ...state,
      persistingChanges: false,
    };
  }),
  on(actions.openCSForPBasket, actions.openCSForList, shopTheList, (state) => {
    const backgroundProducts = backgroundProductsAdapter.removeAll(
      state.backgroundProducts,
    );
    return { ...state, backgroundProducts };
  }),
  on(actions.setBackgroundProducts, (state, { backgroundProducts }) => {
    const { type } = state.source;

    if (type === BasketItemSource.LIST_LOCAL) {
      const distinctBackgroundProducts = backgroundProductsAdapter.upsertMany(
        backgroundProducts,
        state.backgroundProducts,
      );
      return { ...state, backgroundProducts: distinctBackgroundProducts };
    }

    backgroundProductsAdapter.removeAll(state.backgroundProducts);
    const newBackgroundProducts = backgroundProductsAdapter.upsertMany(
      backgroundProducts,
      state.backgroundProducts,
    );

    return { ...state, backgroundProducts: newBackgroundProducts };
  }),
  on(actions.setNextButtonVisible, (state, { showButton }) => ({
    ...state,
    showNextButton: showButton,
  })),
  on(actions.setSkipInventoryCheck, (state, { skipInventoryCheck }) => ({
    ...state,
    skipInventoryCheck,
  })),
  on(
    actions.setProductSource,
    (state, { source, details, additionalUiDetails }) => ({
      ...state,
      source: {
        type: source,
        details: details ?? {},
        additionalUiDetails: additionalUiDetails ?? {},
      },
    }),
  ),
  on(actions.setActiveDeliveryShops, (state, { shops }) => ({
    ...state,
    [activeShopsKey]: shops,
  })),
  /** Copy deliveryPeriods from Cache */
  on(
    actions.copyFromCacheDeliveryPeriods,
    copyDeliveryPeriodsFromCacheBusiness,
  ),
  /** delete FUTURE delivery period */
  on(actions.deleteDeliveryPeriod, deleteFutureDeliveryPeriodBusiness),
  /** add FUTURE delivery period, it will be cached */
  on(
    actions.addDeliveryPeriod,
    (state: BusinessState, { deliveryPeriod, deliveryShops }) =>
      addFutureDeliveryPeriodBusiness(
        state,
        deliveryPeriod,
        true,
        deliveryShops,
      ),
  ),
  on(
    actions.addNowDeliveryPeriod,
    (state: BusinessState, { deliveryPeriod, deliveryShops }) => {
      return {
        ...state,
        ...addNowDeliveryPeriodReducerNew(state, deliveryPeriod, deliveryShops),
      };
    },
  ),
  /** add FUTURE delivery period, it will not be cached, because it is not created by user */
  on(
    actions.addAutoDeliveryPeriod,
    (state: BusinessState, { deliveryPeriod, deliveryShops }) =>
      addFutureDeliveryPeriodBusiness(
        state,
        deliveryPeriod,
        false,
        deliveryShops,
      ),
  ),
  /** select FUTURE delivery period */
  on(actions.selectFutureDeliveryPeriod, selectFutureDeliveryPeriodBusiness),
  /** add BULK delivery period */
  on(actions.addBulkDeliveryPeriod, (state, bulkDeliveryPeriod) =>
    addBulkDeliveryPeriodBusiness(state, bulkDeliveryPeriod),
  ),
  /** delete BULK delivery period */
  on(actions.deleteBulkDeliveryPeriod, deleteBulkDeliveryPeriodBusiness),
  /** select BULK delivery period */
  on(actions.selectBulkDeliveryPeriod, selectBulkDeliveryPeriodBusiness),
  /** select NOW delivery period */
  on(actions.selectNowDeliveryPeriod, selectNowDeliveryPeriodBusiness),
  /** pre-select NOW delivery period */
  on(actions.preSelectNowDeliveryPeriod, preSelectNowDeliveryPeriodBusiness),
  /** copies and nullable from preselectedType to shop's delivery period selection */
  on(actions.setPreselectedDeliveryPeriod, preselectDeliveryPeriodsBusiness),
  on(actions.updateCache, (state, { type, ...updateCacheObj }) =>
    cachedBusiness(state, updateCacheObj),
  ),
  on(
    actions.memoizeProductSizeData,
    (
      state,
      {
        articleId,
        brandId,
        materials,
        scaleSetKeys,
        selectedScale,
        availableScaleSets,
        prepacks,
      },
    ) => {
      return cachedProductDataBusiness(state, {
        [`${brandId} ${articleId}`]: {
          materials,
          scaleSetKeys,
          availableScaleSets,
          selectedScale,
          prepacks,
        },
      });
    },
  ),
  on(actions.setProductNote, productNoteChangeBusiness),
  on(actions.setProductQuantity, productQuantityChangeBusiness),
  on(actions.setProductQuantities, productQuantitiesBusiness),
  on(
    actions.copyQuantities,
    (
      state,
      { data, from: { productId: fromProductId, shopId: fromShopId }, to },
    ) => {
      const deliveryItems = state.deliveryItems;
      const deliveryPeriod = getSelectedDeliveryPeriod(state);

      const isPrePack = !!data.prepacks?.[fromProductId];
      const quantity =
        deliveryItems[fromShopId]?.[deliveryPeriod]?.items[fromProductId]
          ?.quantity ?? 0;
      let values: { [key: string]: number } = {};

      const scaleSets = getScaleSets(data.product as Product);
      if (isPrePack) {
        // this is used for prepacks
        values =
          to.products.reduce(
            (result: { [key: string]: number }, productId: string) => {
              result[productId] = quantity;

              return result;
            },
            {},
          ) ?? {};
      } else {
        // this is used for sizes
        values =
          mapSizeIdQuantitiesToSizeQuantities(
            data?.variants?.[fromProductId],
            deliveryItems,
            fromShopId,
            deliveryPeriod,
            scaleSets,
          ) ?? {};
      }

      const changes = to.products.reduce(
        (newChanges: ProductQuantitiesChange[], productId: string) => {
          return [
            ...newChanges,
            ...to.shops
              // omitting source product itself
              .filter(
                (shopId) =>
                  !(shopId === fromShopId && productId === fromProductId),
              )
              .flatMap((shopId: string) => {
                return Object.keys(values)
                  .map((size) => {
                    const sizeId = isPrePack
                      ? productId
                      : getSizeIdFromSize(
                          (data?.variants?.[productId]
                            ?.children as ProductVariant[]) ?? [],
                          size,
                          scaleSets,
                        );

                    if (!sizeId) return null;

                    return {
                      productId: sizeId,
                      quantity: values[size],
                      deliveryPeriod,
                      accountAddressKey: shopId,
                    };
                  })
                  .filter(isNonNullish);
              }),
          ];
        },
        [],
      );

      return productQuantitiesBusiness(
        { ...state, dirty: true },
        { changes, data },
      );
    },
  ),
  on(actions.copyToShops, (state, { fromShopId, toShopIds, data }) => {
    const copied: BusinessState = toShopIds.reduce((aggr, toShop) => {
      return copyToShopBusiness(fromShopId, toShop, aggr, data);
    }, state);

    return {
      ...state,
      ...copied,
      dirty: true,
    };
  }),
  on(actions.clearShopQuantities, (state, { shopId, data }) => ({
    ...state,
    ...revertShopBusiness(shopId, state, data),
    dirty: true,
  })),
  on(actions.copyToDeliveryPeriod, copyToDeliveryPeriodBusiness),
  on(actions.setPrefilledVisible, setPrefilledVisibleBusiness),
  on(actions.setPrefilledDeliveryItems, setPrefilledDeliveryItemsBusiness),
  on(
    actions.setMasterPrefilledDeliveryItems,
    setMasterPrefilledDeliveryItemsBusiness,
  ),
  on(
    actions.clearPrefilledDeliveryItems,
    (state: BusinessState): BusinessState => {
      return {
        ...state,
        prefilledDeliveryItems: {
          visible: state.prefilledDeliveryItems?.visible,
        },
      };
    },
  ),
  on(actions.copyPrefilledDeliveryItems, setPrefilledCopyBusiness),
  on(setHorizontalView, (state: BusinessState, { value }) => {
    return {
      ...state,
      isSetSourceNeed: value,
    };
  }),
);
